import axios from 'axios'
import { ElMessage } from 'element-plus'
import adaptiveAPI from '@/utils/adaptiveAPI.js'
import {
  handleSessionExpired,
  isSessionExpiryResponse,
  refreshAccessToken
} from '@/utils/sessionExpiryHandler'
import {
  DEFAULT_ECOLOGICAL_ZONE_ZOOM_LEVEL,
  ECOLOGICAL_ZONE_EXACT_GEOMETRY_ZOOM_LEVEL,
  normalizeEcologicalZoneZoomLevel
} from '@/utils/ecologicalZoneRendering'

// 初始化自适应API配置
let service = null;

// 异步初始化API服务
const initializeService = async () => {
  if (service) return service;

  try {
    // 强制初始化 adaptiveAPI (现在它是简化的代理配置)
    await adaptiveAPI.initialize();
    service = adaptiveAPI.getAxiosInstance();
    // console.log('✅ Emergency API 初始化完成，baseURL:', service.defaults.baseURL);
    return service;
  } catch (error) {
    console.error('❌ Emergency API 初始化失败:', error);
    // 回退配置 (也强制使用 /api)
    service = axios.create({
      baseURL: '/api',
      timeout: 180000 
    });

    setupFallbackInterceptors(service);
    return service;
  }
};

// 标记是否已初始化
let initialized = false;

function createSilentAuthError() {
  const silentError = new Error('会话已过期')
  silentError._isAuthError = true
  silentError._silent = true
  return silentError
}

// 设置回退配置的拦截器
const setupFallbackInterceptors = (serviceInstance) => {
  serviceInstance.interceptors.request.use(
    async config => {
      // 前置检查：无 token 时直接拦截 API 请求，不发到后端
      const token = localStorage.getItem('token')
      if (!token) {
        const refreshed = await refreshAccessToken()
        if (refreshed?.token) {
          config.headers = config.headers || {}
          config.headers['Authorization'] = `Bearer ${refreshed.token}`
          return config
        }
        return Promise.reject(createSilentAuthError())
      }
      config.headers = config.headers || {}
      config.headers['Authorization'] = `Bearer ${token}`
      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  serviceInstance.interceptors.response.use(
    response => {
      const res = response.data

      // 如果返回的状态码不是200，说明有错误
      if (res.code && res.code !== 200) {
        ElMessage.error(res.message || '请求失败')
        return Promise.reject(new Error(res.message || '请求失败'))
      }

      return res
    },
    async error => {
      // 401 静默处理：不输出错误日志，避免暴露 API 端点
      if (error._isAuthError || (error.response && error.response.status === 401)) {
        if (!error._isAuthError) {
          const originalConfig = error.config || {}
          if (!originalConfig._authRetry) {
            originalConfig._authRetry = true
            const refreshed = await refreshAccessToken()
            if (refreshed?.token) {
              originalConfig.headers = originalConfig.headers || {}
              originalConfig.headers.Authorization = `Bearer ${refreshed.token}`
              return serviceInstance(originalConfig)
            }
          }
          if (isSessionExpiryResponse(error)) {
            await handleSessionExpired()
          }
        }
        return Promise.reject(createSilentAuthError())
      }

      let message = '网络错误'

      if (error.response) {
        switch (error.response.status) {
          case 400:
            message = '请求参数错误'
            break
          case 403:
            message = '访问被禁止'
            break
          case 404:
            message = '请求的资源不存在'
            break
          case 500:
            message = '服务器内部错误'
            break
          default:
            message = error.response.data?.message || '请求失败'
        }
      } else if (error.code === 'ECONNABORTED') {
        message = '请求超时'
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        message = '网络连接失败，正在尝试切换线路...'
      }

      ElMessage.error(message)
      return Promise.reject(error)
    }
  )
}

// 立即开始初始化
const servicePromise = initializeService();

// 获取服务实例的辅助函数（始终返回最新实例）
const getService = async () => {
  if (!initialized) {
    await servicePromise;
    initialized = true;
  }
  // 每次都从 adaptiveAPI 获取最新的实例，以支持网络环境切换
  return adaptiveAPI.getAxiosInstance() || service;
};

// API接口包装函数
const apiWrapper = (apiFunction) => {
  return async (...args) => {
    const serviceInstance = await getService();
    return apiFunction(serviceInstance, ...args);
  };
};

// 获取主表数据（分页）
export const getDataPage = apiWrapper((service, tableName, params = {}) => {
  return service.get(`/structured/main/${tableName}`, { params })
})

// 中文表名到英文表名的映射
const TABLE_NAME_MAPPING = {
  '应急物资库': 'emergency_materials',
  '物资生产企业': 'material_enterprises',
  '专家支撑单位': 'expert_units',
  '河流基础信息': 'river_info',
  '国省考断面': 'national_provincial_sections',
  '应急空间与设施': 'emergency_facilities',
  '重点环境风险源': 'environmental_risks',
  '危废处置单位': 'hazardous_waste_disposal_units', // 与后端数据库保持一致
  '应急处置单位': 'hazardous_waste_disposal_units', // 历史别名，映射到同一个表
  '固废处置单位': 'hazardous_waste_disposal_units', // 历史显示名称，使用危废数据
  '应急救援队伍': 'emergency_rescue_teams',
  '饮用水源地': 'drinking_water_sources',
  '生态管控区': 'ecological_control_zones',
  '案例库': 'emergency_cases',
  '物质库': 'hazardous_materials'
};

// 获取主表所有数据
export const getData = apiWrapper((service, tableName, options = {}) => {
  const apiTableName = TABLE_NAME_MAPPING[tableName] || tableName;

  // 生态管控区使用专门的API（分批加载所有数据，优化内存）
  if (tableName === '生态管控区') {
    return (async () => {
      try {
        let allZones = []
        let currentPage = 1
        const batchSize = 500 // 减小批次大小
        let hasMore = true
        const includeGeometry = options.includeGeometry === true
        const zoomLevel = normalizeEcologicalZoneZoomLevel(
          options.preferExact ? ECOLOGICAL_ZONE_EXACT_GEOMETRY_ZOOM_LEVEL : options.zoomLevel,
          DEFAULT_ECOLOGICAL_ZONE_ZOOM_LEVEL
        )

        // console.log('🔄 getData开始加载生态管控区...')

        while (hasMore) {
          const query = new URLSearchParams({
            page: String(currentPage),
            pageSize: String(batchSize),
            limit: String(batchSize),
            include_geometry: String(includeGeometry)
          })
          if (includeGeometry) {
            query.set('zoom_level', String(zoomLevel))
          }
          const requestUrl = `ecological-zones/list?${query.toString()}`
          const res = await service.get(requestUrl)

          const batchData = res.data?.data?.list || res.data?.data?.records || []
          allZones = allZones.concat(batchData)

          // console.log(`📦 getData第 ${currentPage} 批: ${batchData.length}条，累计: ${allZones.length}条`)

          if (batchData.length < batchSize) {
            hasMore = false
          } else {
            currentPage++
          }

          // 安全阈值
          if (currentPage > 20) {
            console.warn('⚠️ getData达到最大批次')
            hasMore = false
          }

          // 添加延迟
          if (hasMore) {
            await new Promise(resolve => setTimeout(resolve, 100))
          }
        }

        // console.log(`✅ getData加载生态管控区完成: ${allZones.length}条`)
        return allZones
      } catch (error) {
        // 401 认证错误静默处理，其他错误简要输出
        if (!error._isAuthError) {
          console.warn('生态管控区数据加载失败')
        }
        throw error
      }
    })()
  }

  // 其他数据类型
  const requestUrl = `/structured/main/${apiTableName}/all`;
  // console.log(`📡 加载数据: ${requestUrl}`);
  // console.log(`🔍 表名映射调试: ${tableName} -> ${apiTableName}`);

  return service.get(requestUrl).then(res => {
    // API响应正常

    // 修复数据返回格式：后端返回的格式是 {code, message, data}，我们需要返回其中的data字段
    if (res.data && res.data.data !== undefined) {
      // 使用标准data格式
      return res.data.data;
    }

    // 如果是直接的数组格式（向后兼容）
    return res.data;
  }).catch(error => {
    // console.error(`API请求失败 (${tableName}):`, error);
    throw error;
  });
})

// 获取单条主表数据
export const getDataById = apiWrapper((service, tableName, id) => {
  const englishTableName = TABLE_NAME_MAPPING[tableName] || tableName;
  return service.get(`/structured/main/${englishTableName}/${id}`).then(res => res.data)
})

// 创建主表数据
export const createData = apiWrapper((service, tableName, data) => {
  const englishTableName = TABLE_NAME_MAPPING[tableName] || tableName;
  return service.post(`/structured/main/${englishTableName}`, data)
})

// 批量创建主表数据
export const batchCreateData = apiWrapper((service, tableName, dataList) => {
  const englishTableName = TABLE_NAME_MAPPING[tableName] || tableName;
  return service.post(`/structured/main/${englishTableName}/batch`, dataList)
})

// 更新主表数据
export const updateData = apiWrapper((service, tableName, id, data) => {
  const englishTableName = TABLE_NAME_MAPPING[tableName] || tableName;
  return service.put(`/structured/main/${englishTableName}/${id}`, data)
})

// 删除主表数据
export const deleteData = apiWrapper((service, tableName, id) => {
  const englishTableName = TABLE_NAME_MAPPING[tableName] || tableName;
  return service.delete(`/structured/main/${englishTableName}/${id}`)
})

// 清空指定类型数据
export const deleteDataByType = apiWrapper((service, tableName) => {
  const englishTableName = TABLE_NAME_MAPPING[tableName] || tableName;
  return service.delete(`/structured/main/${englishTableName}`)
})

// 获取明细数据
export const getDetailData = apiWrapper((service, tableName, parentId, options = {}) => {
  const englishTableName = TABLE_NAME_MAPPING[tableName] || tableName;
  const params = {
    parent_uuid: parentId,
    ...(options.params || {})
  };

  return service.get(`/structured/detail/${englishTableName}`, { params }).then(res => {
    // 明细数据API响应

    // 修复数据返回格式：后端返回的格式是 {code, message, data}，我们需要返回其中的data字段
    if (res.data && res.data.data !== undefined) {
      // 明细数据使用标准格式
      return res.data.data;
    }

    // 如果是直接的数组格式（向后兼容）
    return res.data;
  })
})

// 获取应急明细数据（兼容旧接口）
export const getEmergencyDetails = apiWrapper((service, tableName, parentId) => {
  return getDetailData(tableName, parentId)
})

// 创建明细数据
export const createDetailData = apiWrapper((service, tableName, data) => {
  const englishTableName = TABLE_NAME_MAPPING[tableName] || tableName;
  return service.post(`/structured/detail/${englishTableName}`, data)
})

// 更新明细数据
export const updateDetailData = apiWrapper((service, tableName, id, data) => {
  const englishTableName = TABLE_NAME_MAPPING[tableName] || tableName;
  return service.put(`/structured/detail/${englishTableName}/${id}`, data)
})

// 删除明细数据
export const deleteDetailData = apiWrapper((service, tableName, id) => {
  const englishTableName = TABLE_NAME_MAPPING[tableName] || tableName;
  return service.delete(`/structured/detail/${englishTableName}/${id}`)
})

// 上传主表文件
export const uploadMainData = apiWrapper(async (service, dataType, file, sessionId = null) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('dataType', dataType)
  
  // 如果提供了会话ID，添加到表单数据中
  if (sessionId) {
    formData.append('sessionId', sessionId)
  }
  
  // 根据文件大小动态设置超时时间 - 大幅增加超时时间
  const fileSizeMB = file.size / (1024 * 1024)
  let timeout = 300000 // 基础5分钟
  
  if (fileSizeMB > 0.5) {
    // 每MB增加2分钟，最大60分钟
    timeout = Math.min(3600000, 300000 + (fileSizeMB * 120000))
  }
  
  if (process.env.NODE_ENV === 'development') {
    // 主表上传开始
  }
  
  return service.post('/upload/main', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    timeout: timeout,
    // 添加上传进度监听
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
      if (process.env.NODE_ENV === 'development') {
        // 上传进度更新
      }
      // 触发自定义事件用于UI更新
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('uploadProgress', {
          detail: {
            sessionId,
            progress: percentCompleted,
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            type: 'upload'
          }
        }))
      }
    }
  }).then(res => {
    // 处理上传响应，确保返回正确的格式
    if (res && res.data) {
      return res.data
    }
    return res
  })
})

// 上传明细文件
export const uploadDetailData = apiWrapper((service, dataType, parentId, formData) => {
  formData.append('dataType', dataType)
  formData.append('parentId', parentId)
  
  return service.post('/upload/detail', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    timeout: 120000 // 2分钟超时
  })
})

// 批量上传明细文件
export const batchUploadDetailData = apiWrapper(async (service, dataType, file, sessionId = null) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('dataType', dataType)
  
  // 如果提供了会话ID，添加到表单数据中
  if (sessionId) {
    formData.append('sessionId', sessionId)
  }
  
  // 大幅延长超时时间以处理大量数据
  const fileSizeMB = file.size / (1024 * 1024)
  let timeout = 600000 // 基础10分钟
  
  if (fileSizeMB > 0.5) {
    timeout = Math.min(3600000, 600000 + (fileSizeMB * 180000)) // 每MB增加3分钟，最多60分钟
  }
  
  if (process.env.NODE_ENV === 'development') {
    // 批量明细上传开始
  }
  
  return service.post('/upload/detail/batch', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    timeout: timeout,
    // 添加上传进度监听
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
      if (process.env.NODE_ENV === 'development') {
        // 批量明细上传进度更新
      }
      // 触发自定义事件用于UI更新
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('uploadProgress', {
          detail: {
            sessionId,
            progress: percentCompleted,
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            type: 'batch_upload'
          }
        }))
      }
    }
  }).then(res => res)
})

// 连接进度监控
export const connectProgressMonitor = apiWrapper((service, sessionId) => {
  return new EventSource(`/api/upload/progress/${sessionId}`)
})

// 导入选中的重复记录
export const importSelectedDuplicates = apiWrapper((service, dataType, records) => {
  return service.post('/upload/import-duplicates', {
    dataType,
    records,
    forceOverwrite: true
  }, {
    timeout: 60000 // 60秒超时
  }).then(res => {
    if (process.env.NODE_ENV === 'development') {
      // 导入重复记录完成
    }
    return res.data || res;
  })
})

// 强制导入数据（覆盖重复记录） - 使用相同的API作为importSelectedDuplicates
export const forceImportData = apiWrapper((service, dataType, records) => {
  return service.post('/upload/import-duplicates', {
    dataType,
    records,
    forceOverwrite: true
  }, {
    timeout: 60000 // 60秒超时
  }).then(res => {
    if (process.env.NODE_ENV === 'development') {
      // 强制导入完成
    }
    return res.data || res;
  })
})

// 获取带分页的数据
export const getDataWithPagination = apiWrapper((service, dataType, params = {}) => {
  // 从params对象中提取参数
  const { page = 1, pageSize = 20, search = '', ...otherParams } = params;

  const requestParams = {
    page,
    pageSize,
    search: search.trim(),
    ...otherParams
  };

  const englishTableName = TABLE_NAME_MAPPING[dataType] || dataType;

  return service.get(`/structured/main/${englishTableName}/paginated`, { params: requestParams })
    .then(res => {
      // console.log(`✅ ${dataType} 分页API响应:`, res)

      // 统一返回格式 {data: [], total: 0}
      // 处理多种可能的响应格式
      let actualData = null;
      let actualTotal = 0;

      // 情况1: 后端返回 {code: 200, data: {data: [...], total: 123}}
      if (res.data?.data?.data && Array.isArray(res.data.data.data)) {
        actualData = res.data.data.data;
        actualTotal = res.data.data.total || res.data.data.count || 0;
      }
      // 情况2: 后端返回 {data: {data: [...], total: 123}}
      else if (res.data?.data && Array.isArray(res.data.data)) {
        actualData = res.data.data;
        actualTotal = res.data.total || res.data.count || 0;
      }
      // 情况3: 后端返回 {data: [...], total: 123}
      else if (res.data && Array.isArray(res.data)) {
        actualData = res.data;
        actualTotal = res.total || 0;
      }
      // 情况4: 兜底处理
      else {
        actualData = [];
        actualTotal = 0;
      }

      return {
        data: actualData,
        total: Number(actualTotal) || 0
      }
    })
    .catch(error => {
      // console.error(`❌ ${dataType} 分页API请求失败:`, error)
      throw error
    })
})

// 添加新数据
export const addData = apiWrapper((service, dataType, data) => {
  // console.log(`➕ 新增数据API请求: addData('${dataType}')`, data)
  const englishTableName = TABLE_NAME_MAPPING[dataType] || dataType;
  
  return service.post(`/structured/main/${englishTableName}`, data)
    .then(res => {
      // console.log(`✅ ${dataType} 新增成功:`, res)
      return res
    })
    .catch(error => {
      // console.error(`❌ ${dataType} 新增失败:`, error)
      throw error
    })
})

// 批量删除数据
export const batchDeleteData = apiWrapper((service, dataType, ids) => {
  // console.log(`🗑️ 批量删除API请求: batchDeleteData('${dataType}')`, ids)
  const englishTableName = TABLE_NAME_MAPPING[dataType] || dataType;
  
  return service.delete(`/structured/main/${englishTableName}/batch`, {
    data: { ids }
  })
    .then(res => {
      // console.log(`✅ ${dataType} 批量删除成功:`, res)
      return res
    })
    .catch(error => {
      // console.error(`❌ ${dataType} 批量删除失败:`, error)
      throw error
    })
})

// 批量删除明细数据
export const batchDeleteDetailData = apiWrapper((service, dataType, ids) => {
  // console.log(`🗑️ 明细批量删除API请求: batchDeleteDetailData('${dataType}')`, ids)
  const englishTableName = TABLE_NAME_MAPPING[dataType] || dataType;
  
  return service.delete(`/structured/detail/${englishTableName}/batch`, {
    data: { ids }
  })
    .then(res => {
      // console.log(`✅ ${dataType} 明细批量删除成功:`, res)
      return res
    })
    .catch(error => {
      // console.error(`❌ ${dataType} 明细批量删除失败:`, error)
      throw error
    })
})

// 健康检查
export const healthCheck = apiWrapper((service) => {
  return service.get('/structured/health')
})

// 导出失败记录（不需要网络请求，直接导出）
export const exportFailedRecords = (failedRecords, dataType, filename) => {
  // 创建CSV内容
  const headers = ['行号', '失败原因', '名称', '经度', '纬度']
  const csvContent = [
    headers.join(','),
    ...failedRecords.map(record => [
      record.rowNumber || record.row || '',
      (record.reasons || [record.reason] || []).join('; '),
      record.data?.名称 || '',
      record.data?.经度 || '',
      record.data?.纬度 || ''
    ].map(field => `"${field}"`).join(','))
  ].join('\n')
  
  // 创建下载链接
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
  
  return Promise.resolve()
}

// 导出服务实例给其他模块使用
export { servicePromise }

// 应急资源数据查询（重构版本使用）
export const getEmergencyData = apiWrapper(async (service, dataType) => {
  // console.log(`🔍 getEmergencyData请求: ${dataType}`);
  const result = await getData(dataType)
  // console.log(`✅ getEmergencyData成功获取 ${dataType} 数据:`, {
  //   数据类型: typeof result,
  //   是否数组: Array.isArray(result),
  //   数据长度: result?.length || 0
  // });

  return {
    data: result,
    success: true
  }
});

// 获取统计数据（AdminView专用，使用正确的路由）
export const getStatistics = apiWrapper((service, options = {}) => {
  // 使用正确的统计路由 /structured/stats
  const params = {
    ...(options.params || {})
  };

  return service.get('/structured/stats', { params })
    .then(res => {
      // console.log(`✅ 统计API响应:`, res)
      // 后端返回格式: {code: 200, message: "统计成功", data: {应急物资库: 465, ...}}
      // 直接返回data字段中的统计数据
      if (res.data) {
        return res.data
      }
      return res
    })
    .catch(error => {
      // 401 认证错误静默处理
      if (!error._isAuthError) {
        console.warn('统计API请求失败')
      }
      throw error
    })
})

// 保持向后兼容
export default getService
